import React from "react";

import { GridItem } from "commons/ui/Grid";
import Paper from "commons/ui/Paper";

import { useBlockGroupContext } from "components/form/Blocks";
import SectionFiveImagesFifteenTextsForm from "components/form/blocks/SectionFiveImagesFifteenTextsForm";
import SectionFourImagesVerticalForm from "components/form/blocks/SectionFourImagesVerticalForm";
import SectionGalleryForm from "components/form/blocks/SectionGalleryForm";
import SectionImageTextForm from "components/form/blocks/SectionImageTextForm";
import SectionOneForm from "components/form/blocks/SectionOneForm";
import SectionOneImageFiveTextsForm from "components/form/blocks/SectionOneImageFiveTextsForm";
import SectionOneImageForm from "components/form/blocks/SectionOneImageForm";
import SectionOneImageNineTextsForm from "components/form/blocks/SectionOneImageNineTextsForm";
import SectionOneImageOneTextForm from "components/form/blocks/SectionOneImageOneTextForm";
import SectionOneTitleForm from "components/form/blocks/SectionOneTitleForm";
import SectionOneTitleTextsForm from "components/form/blocks/SectionOneTitleTextsForm";
import SectionProductFooterForm from "components/form/blocks/SectionProductFooter";
import SectionProductHighlightForm from "components/form/blocks/SectionProductHighlightForm";
import SectionSevenTextsForm from "components/form/blocks/SectionSevenTextsForm";
import SectionThreeImagesTwoTexts from "components/form/blocks/SectionThreeImagesTwoTexts";
import SectionThreeImagesVerticalForm from "components/form/blocks/SectionThreeImagesVerticalForm";
import SectionThreeParagraphsForm from "components/form/blocks/SectionThreeParagraphsForm";
import SectionTitleHomePageForm from "components/form/blocks/SectionTitleHomePageForm";
import SectionTitleSeparatorForm from "components/form/blocks/SectionTitleSeparatorForm";
import SectionTitleSubtitleParagraphForm from "components/form/blocks/SectionTitleSubtitleParagraphForm";
import SectionTwoForm from "components/form/blocks/SectionTwoForm";
import SectionTwoImagesFiveTextsForm from "components/form/blocks/SectionTwoImagesFiveTextsForm";
import SectionTwoImagesForm from "components/form/blocks/SectionTwoImagesForm";
import SectionTwoImagesTextsButtonsForm from "components/form/blocks/SectionTwoImagesTextsButtonsForm";
import SectionVideoForm from "components/form/blocks/SectionVideoForm";

import { useSaveActionsContext } from "context/save";

import useBlockGroupCompositionApi from "hooks/api/private/useBlockGroupCompositionApi";

import { BlockGroupCompositionBody } from "types/api/blockGroupComposition";
import { BlockEnum } from "types/enums";

function BlocksBuilding({
    setSingleBlockFocus,
    draggable,
    divProps,
}: {
    setSingleBlockFocus: (value: boolean) => void;
    draggable: boolean;
    divProps: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
}) {
    const {
        blockGroupCompositionId,
        blockId,
        graphicalOrder,
        blockGroupId,
        focusedBlock,
        blockWithErrors,
    } = useBlockGroupContext();

    const { addSaveAction } = useSaveActionsContext();

    const { updateBlockGroupComposition } = useBlockGroupCompositionApi();

    React.useEffect(() => {
        addSaveAction(`${blockGroupCompositionId}-update-graphical-order`, () => {
            const promises: Promise<unknown>[] = [];

            const blocksComposition: BlockGroupCompositionBody = {
                block_group_id: blockGroupId,
                block_id: blockId as BlockEnum,
                graphical_order: graphicalOrder ?? 0,
            };

            const [updateBlockApi] = updateBlockGroupComposition(
                { id: blockGroupCompositionId },
                blocksComposition,
            );

            promises.push(updateBlockApi);

            return Promise.all(promises);
        });
    }, [
        updateBlockGroupComposition,
        addSaveAction,
        blockGroupCompositionId,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    const focusedStyle = React.useMemo(() => {
        if (blockWithErrors) {
            return {
                outline: "2px solid rgb(255, 0, 0)",
                boxShadow: "0 0 15px rgba(255, 0, 0, 0.5)",
            };
        } else if (focusedBlock) {
            return {
                outline: "2px solid rgb(56, 56, 56)",
                boxShadow: "0 0 15px rgba(56, 56, 56, 0.5)",
            };
        } else {
            return {};
        }
    }, [blockWithErrors, focusedBlock]);

    return (
        <GridItem>
            <div
                {...divProps}
                draggable={draggable}
                onFocus={() => setSingleBlockFocus(true)}
                onBlur={() => setSingleBlockFocus(false)}
            >
                <Paper className={"module-form"} style={focusedStyle}>
                    {blockId === BlockEnum.SectionOne && <SectionOneForm />}

                    {(blockId === BlockEnum.SectionVideo ||
                        blockId === BlockEnum.SectionVideoFull) && (
                        <SectionVideoForm blockId={blockId} />
                    )}

                    {blockId === BlockEnum.SectionOneTitleTexts && <SectionOneTitleTextsForm />}

                    {blockId === BlockEnum.SectionTitleHomePage && (
                        <SectionTitleHomePageForm blockId={blockId} />
                    )}

                    {blockId === BlockEnum.SectionThreeParagraphs && <SectionThreeParagraphsForm />}

                    {blockId === BlockEnum.SectionProductFooter && <SectionProductFooterForm />}

                    {blockId === BlockEnum.SectionFourImagesVertical && (
                        <SectionFourImagesVerticalForm />
                    )}

                    {blockId === BlockEnum.SectionThreeImagesTwoTexts && (
                        <SectionThreeImagesTwoTexts />
                    )}

                    {blockId === BlockEnum.SectionThreeImagesVertical && (
                        <SectionThreeImagesVerticalForm />
                    )}

                    {blockId === BlockEnum.SectionProductDescription && (
                        <SectionOneImageNineTextsForm />
                    )}

                    {(blockId === BlockEnum.SectionOneTitleTwoTexts ||
                        blockId === BlockEnum.SectionTitleWithRightTextsAndButton) && (
                        <SectionOneTitleForm blockId={blockId} />
                    )}

                    {blockId === BlockEnum.SectionTitleSubtitleParagraph && (
                        <SectionTitleSubtitleParagraphForm />
                    )}

                    {(blockId === BlockEnum.SectionTwo ||
                        blockId === BlockEnum.SectionVideoCompany) && (
                        <SectionTwoForm blockId={blockId} />
                    )}

                    {(blockId === BlockEnum.SectionTitleSeparator ||
                        blockId === BlockEnum.SectionTitleVerticalSeparator) && (
                        <SectionTitleSeparatorForm blockId={blockId} />
                    )}

                    {(blockId === BlockEnum.SectionTitleButtonWithFourPaper ||
                        blockId === BlockEnum.SectionTitleWithFiveImages) && (
                        <SectionFiveImagesFifteenTextsForm blockId={blockId} />
                    )}

                    {blockId === BlockEnum.SectionSevenTexts && <SectionSevenTextsForm />}

                    {(blockId === BlockEnum.SectionOneImageCentered ||
                        blockId === BlockEnum.SectionOneImageFull) && (
                        <SectionOneImageForm blockId={blockId} />
                    )}

                    {(blockId === BlockEnum.SectionTextOnLeftBackground ||
                        blockId === BlockEnum.SectionTextOnRightBackground) && (
                        <SectionTwoImagesFiveTextsForm blockId={blockId} fourTexts />
                    )}

                    {(blockId === BlockEnum.SectionTwoImagesRightTextsButtons ||
                        blockId === BlockEnum.SectionTwoImagesLeftTextsButtons) && (
                        <SectionTwoImagesTextsButtonsForm />
                    )}

                    {(blockId === BlockEnum.SectionGallery ||
                        blockId === BlockEnum.SectionGalleryFull) && (
                        <SectionGalleryForm blockId={blockId} />
                    )}

                    {blockId === BlockEnum.SectionProductHighlight && (
                        <SectionProductHighlightForm blockId={blockId} />
                    )}

                    {(blockId === BlockEnum.SectionVideoSideLeft ||
                        blockId === BlockEnum.SectionVideoSideRight ||
                        blockId === BlockEnum.SectionOneImageRightOneTexts ||
                        blockId === BlockEnum.SectionOneImageLeftOneTexts ||
                        blockId === BlockEnum.SectionTopTitleWithBackground) && (
                        <SectionOneImageOneTextForm blockId={blockId} />
                    )}

                    {(blockId === BlockEnum.SectionTwoImagesVertical ||
                        blockId === BlockEnum.SectionTwoImagesRightBig ||
                        blockId === BlockEnum.SectionTwoImagesLeftBig) && (
                        <SectionTwoImagesForm blockId={blockId} />
                    )}

                    {(blockId === BlockEnum.SectionOneImageTextCentered ||
                        blockId === BlockEnum.SectionOneImageTextBackgroundCentered ||
                        blockId === BlockEnum.SectionOneImageTextBackground ||
                        blockId === BlockEnum.SectionOneImageTextFullBackground) && (
                        <SectionImageTextForm blockId={blockId} />
                    )}

                    {(blockId === BlockEnum.SectionOneImageLeftFiveTexts ||
                        blockId === BlockEnum.SectionOneImageRightFiveTexts ||
                        blockId === BlockEnum.SectionOneSmallImageLeftText ||
                        blockId === BlockEnum.SectionOneSmallImageRightText ||
                        blockId === BlockEnum.SectionTitleSubtitleButton ||
                        blockId === BlockEnum.SectionPaperWithTitleButtonAndRightImage ||
                        blockId === BlockEnum.SectionTitleRightTextsButtonsOnBackground ||
                        blockId === BlockEnum.SectionTextsWithButtonAndRightImage ||
                        blockId === BlockEnum.SectionOneImageThreeTexts) && (
                        <SectionOneImageFiveTextsForm blockId={blockId} />
                    )}
                </Paper>
            </div>
        </GridItem>
    );
}

export default BlocksBuilding;
